/**
 * Evolu-backed counter source for NUT-13 deterministic Cashu secrets.
 *
 * Implements the cashu-ts CounterSource interface to persist counter
 * state in Evolu's cashuCounter table. Counters are monotonic per keyset
 * and never reuse values, ensuring deterministic secrets are unique.
 *
 * IMPORTANT: This is NOT a React hook. It receives the Evolu instance
 * directly and uses evolu.insert()/evolu.update() for persistence.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module cashu/utils/counter-source
 */

import type { Evolu } from '@evolu/common';
import { NonEmptyString1000, getOrThrow, type Result } from '@evolu/common';
import type { CounterSource, CounterRange } from '@cashu/cashu-ts';
import type { SatsflowSchema } from '../../../db/database';

/** Row shape from an Evolu cashuCounter query. */
export interface CashuCounterRow {
  readonly id: string;
  readonly keysetId: string;
  readonly mintUrl: string;
  readonly nextCounter: number;
}

/**
 * Evolu-backed implementation of the cashu-ts CounterSource interface.
 *
 * Maintains an in-memory cache of counters for fast reads, with
 * write-through persistence to Evolu's cashuCounter table. Each keyset
 * has an independent monotonic counter.
 */
export class EvoluCounterSource implements CounterSource {
  private readonly counters: Map<string, number> = new Map();
  private readonly rowIds: Map<string, string> = new Map();
  private readonly mintUrls: Map<string, string> = new Map();
  private readonly evolu: Evolu<SatsflowSchema>;

  constructor(evolu: Evolu<SatsflowSchema>, initial?: Record<string, number>) {
    this.evolu = evolu;
    if (initial) {
      for (const [keysetId, value] of Object.entries(initial)) {
        this.counters.set(keysetId, value);
      }
    }
  }

  /**
   * Create an EvoluCounterSource pre-populated from Evolu query results.
   *
   * @param evolu - The Evolu instance for persistence
   * @param rows - cashuCounter rows from an Evolu query
   * @returns A new EvoluCounterSource with counters loaded from the rows
   */
  static loadFromEvolu(
    evolu: Evolu<SatsflowSchema>,
    rows: readonly CashuCounterRow[],
  ): EvoluCounterSource {
    const source = new EvoluCounterSource(evolu);
    for (const row of rows) {
      source.counters.set(row.keysetId, row.nextCounter);
      source.rowIds.set(row.keysetId, row.id);
      source.mintUrls.set(row.keysetId, row.mintUrl);
    }
    return source;
  }

  /**
   * Set the mint URL for a keyset. Required before the first insert
   * for a keyset that doesn't yet have a row in Evolu.
   *
   * @param keysetId - The keyset ID
   * @param mintUrl - The mint URL this keyset belongs to
   */
  setMintUrl(keysetId: string, mintUrl: string): void {
    this.mintUrls.set(keysetId, mintUrl);
  }

  /**
   * Reserve n counters for a keyset.
   *
   * If n === 0, returns the current counter without mutation (peek).
   * Otherwise, advances the counter by n and persists to Evolu.
   *
   * @param keysetId - The keyset to reserve counters for
   * @param n - Number of counters to reserve (0 = peek)
   * @returns The starting counter and count reserved
   */
  async reserve(keysetId: string, n: number): Promise<CounterRange> {
    const current = this.counters.get(keysetId) ?? 0;

    if (n === 0) {
      return { start: current, count: 0 };
    }

    const next = current + n;
    this.counters.set(keysetId, next);
    await this.persistCounter(keysetId, next);

    return { start: current, count: n };
  }

  /**
   * Ensure the next counter for a keyset is at least minNext.
   * Only advances; never decrements.
   *
   * @param keysetId - The keyset to advance
   * @param minNext - The minimum value for the next counter
   */
  async advanceToAtLeast(keysetId: string, minNext: number): Promise<void> {
    const current = this.counters.get(keysetId) ?? 0;
    if (minNext > current) {
      this.counters.set(keysetId, minNext);
      await this.persistCounter(keysetId, minNext);
    }
  }

  /**
   * Return a snapshot of all counter values.
   *
   * @returns Object mapping keysetId to next counter value
   */
  async snapshot(): Promise<Record<string, number>> {
    return await Promise.resolve(Object.fromEntries(this.counters));
  }

  /**
   * Set the counter for a keyset and persist to Evolu.
   *
   * @param keysetId - The keyset to set
   * @param next - The new counter value
   */
  async setNext(keysetId: string, next: number): Promise<void> {
    this.counters.set(keysetId, next);
    await this.persistCounter(keysetId, next);
  }

  /**
   * Persist a counter value to Evolu. Updates existing row or inserts new one.
   */
  private async persistCounter(keysetId: string, next: number): Promise<void> {
    await Promise.resolve(); // Evolu mutations are synchronous but interface requires async
    const existingRowId = this.rowIds.get(keysetId);

    if (existingRowId) {
      this.evolu.update('cashuCounter', {
        id: existingRowId as never,
        nextCounter: next as never,
      });
    } else {
      const mintUrl = this.mintUrls.get(keysetId) ?? '';
      const result = this.evolu.insert('cashuCounter', {
        keysetId: getOrThrow(NonEmptyString1000.from(keysetId)),
        mintUrl: getOrThrow(NonEmptyString1000.from(mintUrl)),
        nextCounter: next as never,
      }) as unknown as Result<{ readonly id: string }, unknown>;
      const insertResult = 'id' in result ? result : getOrThrow(result);
      this.rowIds.set(keysetId, String(insertResult.id));
    }
  }
}
